import { type ReactNode } from "react";
import { BookOpen, Heart, Mail, Scale, Shield, type LucideIcon } from "lucide-react";

import { Container } from "@/platform/layout/container";
import { Badge } from "@/platform/ui/badge";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

type Tone = "violet" | "pink" | "mint" | "yellow" | "coral" | "sky";

const TONE_BG: Record<Tone, string> = {
  violet: "bg-[color:var(--color-accent-soft)] text-[color:var(--color-accent-soft-foreground)]",
  pink: "bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)]",
  mint: "bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)]",
  yellow: "bg-[color:var(--color-yellow-soft)] text-[color:var(--color-yellow-deep)]",
  coral: "bg-[color:var(--color-coral-soft)] text-[color:var(--color-coral-deep)]",
  sky: "bg-[color:var(--color-sky-soft)] text-[color:var(--color-sky-deep)]",
};

const TONE_BADGE: Record<Tone, "accent" | "pink" | "mint" | "yellow" | "coral" | "sky"> = {
  violet: "accent",
  pink: "pink",
  mint: "mint",
  yellow: "yellow",
  coral: "coral",
  sky: "sky",
};

function LegalShell({
  kicker,
  icon: Icon,
  tone,
  title,
  description,
  children,
}: {
  kicker: string;
  icon: LucideIcon;
  tone: Tone;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-12 top-24 w-10 opacity-50 animate-float"
      />

      <Container size="default" className="relative py-20 md:py-28">
        <header className="animate-fade-up max-w-2xl">
          <div className="flex items-center gap-3">
            <span className={`chip size-11 ${TONE_BG[tone]}`}>
              <Icon className="size-5" aria-hidden />
            </span>
            <Badge variant={TONE_BADGE[tone]}>{kicker}</Badge>
          </div>
          <h1 className="mt-6 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            {title}
          </h1>
          {description ? (
            <p className="mt-4 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty">
              {description}
            </p>
          ) : null}
        </header>
        <div className="mt-12 max-w-2xl">{children}</div>
      </Container>
    </div>
  );
}

function ContactMail({ mail }: { mail: string }) {
  return (
    <a
      href={`mailto:${mail}`}
      className="link font-medium text-[color:var(--color-accent)]"
    >
      {mail}
    </a>
  );
}

export function TermsPage() {
  return (
    <LegalShell
      kicker="Termos de uso"
      icon={Scale}
      tone="violet"
      title={
        <>
          Termos de uso · <span className="text-gradient-accent">v1</span>
        </>
      }
      description="Vigentes a partir de 17/05/2026. Em revisão pela equipe de Compliance até o lançamento."
    >
      <div className="space-y-5 text-[length:var(--text-body)] text-[color:var(--color-foreground)] leading-[var(--text-body--line-height)]">
        <p>
          Este documento ainda está em revisão pela equipe de Compliance. A versão definitiva será
          publicada antes do lançamento público da plataforma e estará versionada neste endereço.
        </p>
        <p>
          Em síntese, o serviço destina-se à preparação para o vestibular de Psicologia da
          Faculdade Israelita de Ciências da Saúde Albert Einstein e cursos correlatos. Não há
          garantia de aprovação. O uso pressupõe responsabilidade do candidato pelo estudo e pelos
          cuidados com a saúde mental durante o processo seletivo.
        </p>
        <p className="border-t border-[--color-border] pt-5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Contato: <ContactMail mail="juridico@neuronix.dmzdigital.com.br" />
        </p>
      </div>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      kicker="Política de privacidade"
      icon={Shield}
      tone="mint"
      title={
        <>
          Privacidade · <span className="text-gradient-accent">LGPD</span>
        </>
      }
      description="Vigente a partir de 17/05/2026. Tratamos dados pessoais sob a Lei 13.709/2018."
    >
      <div className="space-y-5 text-[length:var(--text-body)] text-[color:var(--color-foreground)] leading-[var(--text-body--line-height)]">
        <p>
          A versão completa desta política será publicada com a abertura ao público. Em resumo,
          quatro premissas governam tudo:
        </p>
        <ul className="space-y-3">
          {[
            { label: "Minimização", body: "Coletamos apenas o necessário para o serviço.", tone: "violet" as const },
            { label: "Consentimento granular", body: "Uso de redação para melhorar modelos é opt-in, revogável a qualquer momento.", tone: "pink" as const },
            { label: "Direito ao esquecimento", body: "Você pode exportar e excluir todos os seus dados.", tone: "mint" as const },
            { label: "Criptografia", body: "Redações e discursivas são criptografadas em repouso. Nenhum operador acessa sem ordem judicial ou consentimento.", tone: "yellow" as const },
          ].map((it) => (
            <li
              key={it.label}
              className="rounded-lg border border-[--color-border] bg-[color:var(--color-card)] p-5"
            >
              <Badge variant={TONE_BADGE[it.tone]}>{it.label}</Badge>
              <p className="mt-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                {it.body}
              </p>
            </li>
          ))}
        </ul>
        <p className="border-t border-[--color-border] pt-5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Contato: <ContactMail mail="privacidade@neuronix.dmzdigital.com.br" />
        </p>
      </div>
    </LegalShell>
  );
}

export function ContactPage() {
  const contacts: Array<{ label: string; mail: string; tone: Tone; icon: LucideIcon }> = [
    { label: "Produto e dúvidas", mail: "ola@neuronix.dmzdigital.com.br", tone: "violet", icon: Mail },
    { label: "Bolsa e acessibilidade", mail: "bolsa@neuronix.dmzdigital.com.br", tone: "mint", icon: Heart },
    { label: "Privacidade e LGPD", mail: "privacidade@neuronix.dmzdigital.com.br", tone: "pink", icon: Shield },
    { label: "Jurídico", mail: "juridico@neuronix.dmzdigital.com.br", tone: "yellow", icon: Scale },
  ];
  return (
    <LegalShell
      kicker="Contato"
      icon={Mail}
      tone="pink"
      title="Como falar com a gente."
      description="Respondemos em até dois dias úteis. Mensagens com urgência clínica devem ser endereçadas a serviços de saúde mental — o CVV atende 24h pelo 188."
    >
      <div className="space-y-4">
        {contacts.map((c) => (
          <a
            key={c.mail}
            href={`mailto:${c.mail}`}
            className="lift group flex items-center gap-4 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)] transition-colors hover:border-[--color-accent]/40"
          >
            <span className={`chip size-12 shrink-0 ${TONE_BG[c.tone]}`}>
              <c.icon className="size-5" aria-hidden />
            </span>
            <div className="flex-1">
              <p className="text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                {c.label}
              </p>
              <p className="mt-1 font-display text-[length:var(--text-h4)] font-semibold text-[color:var(--color-foreground)] group-hover:text-[color:var(--color-accent)]">
                {c.mail}
              </p>
            </div>
          </a>
        ))}
      </div>
    </LegalShell>
  );
}

export function CitationPolicyPage() {
  return (
    <LegalShell
      kicker="Política de citação"
      icon={BookOpen}
      tone="yellow"
      title={
        <>
          Como tratamos <span className="text-gradient-accent">provas oficiais.</span>
        </>
      }
      description="A plataforma usa exclusivamente fontes oficiais, públicas, licenciadas ou autorizadas."
    >
      <div className="space-y-5 text-[length:var(--text-body)] text-[color:var(--color-foreground)] leading-[var(--text-body--line-height)]">
        <p>
          Para questões originais publicadas por terceiros (Vunesp, Einstein, cursinhos),
          trabalhamos com citações curtas, sempre com link para a fonte oficial.
        </p>
        <p>
          Questões disponibilizadas no banco da plataforma são autorais, inspiradas nos padrões
          observados após análise estatística do corpus expandido. Cada questão autoral é marcada
          com identificação clara e versão.
        </p>
        <p>
          Materiais oficiais (manual do candidato, edital, conteúdo programático) são citados com
          atribuição direta à fonte. Nada é republicado sem licença ou autorização específica.
        </p>
        <p className="border-t border-[--color-border] pt-5 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
          Em caso de dúvida sobre uso de qualquer conteúdo, escreva para{" "}
          <ContactMail mail="juridico@neuronix.dmzdigital.com.br" />.
        </p>
      </div>
    </LegalShell>
  );
}
